import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { HLJSApi } from 'highlight.js';

let hljsModule: HLJSApi | null = null;
let hljsLoadPromise: Promise<void> | null = null;

export function isHighlighterReady(): boolean {
  return hljsModule !== null;
}

export async function loadHighlighter(): Promise<void> {
  if (hljsModule) return;
  if (hljsLoadPromise) return hljsLoadPromise;
  hljsLoadPromise = (async () => {
    const [core, js, ts, py, jsonLang, bashLang] = await Promise.all([
      import(/* webpackChunkName: "hljs" */ 'highlight.js/lib/core'),
      import(/* webpackChunkName: "hljs" */ 'highlight.js/lib/languages/javascript'),
      import(/* webpackChunkName: "hljs" */ 'highlight.js/lib/languages/typescript'),
      import(/* webpackChunkName: "hljs" */ 'highlight.js/lib/languages/python'),
      import(/* webpackChunkName: "hljs" */ 'highlight.js/lib/languages/json'),
      import(/* webpackChunkName: "hljs" */ 'highlight.js/lib/languages/bash'),
    ]);
    const hljs = core.default;
    hljs.registerLanguage('javascript', js.default);
    hljs.registerLanguage('typescript', ts.default);
    hljs.registerLanguage('python', py.default);
    hljs.registerLanguage('json', jsonLang.default);
    hljs.registerLanguage('bash', bashLang.default);
    hljsModule = hljs;
  })();
  return hljsLoadPromise;
}

const createDOMPurify = typeof window !== 'undefined' ? DOMPurify : null;
let purifierInstance: ReturnType<typeof DOMPurify> | null = null;

marked.setOptions({
  gfm: true,
  breaks: true,
});

function getPurifier() {
  if (!purifierInstance && createDOMPurify && typeof window !== 'undefined') {
    purifierInstance = createDOMPurify(window);
  }
  return purifierInstance;
}

// Simple bounded cache to avoid re-parsing identical markdown
const renderCache = new Map<string, string>();
const CACHE_MAX = 200;

export function renderMarkdown(text: string): string {
  if (!text) return '';
  const cached = renderCache.get(text);
  if (cached !== undefined) return cached;
  const renderer = new marked.Renderer();
  renderer.code = ({ text: code, lang }: { text: string; lang?: string }) => {
    if (hljsModule && lang && hljsModule.getLanguage(lang)) {
      const highlighted = hljsModule.highlight(code, { language: lang }).value;
      return `<pre><code class="hljs language-${lang}">${highlighted}</code></pre>`;
    }
    if (hljsModule) {
      try {
        const highlighted = hljsModule.highlightAuto(code).value;
        return `<pre><code class="hljs">${highlighted}</code></pre>`;
      } catch {
        /* fall through to escaped fallback */
      }
    }
    const escaped = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `<pre><code>${escaped}</code></pre>`;
  };
  renderer.link = (...args: unknown[]) => {
    let href = '';
    let title = '';
    let text = '';
    if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null) {
      const token = args[0] as { href?: string; title?: string; text?: string };
      href = token.href || '';
      title = token.title || '';
      text = token.text || '';
    } else {
      href = (args[0] as string) || '';
      title = (args[1] as string) || '';
      text = (args[2] as string) || '';
    }
    const isSafe = /^(https?:\/\/|\/[^/]|#|mailto:)/i.test(href);
    if (!isSafe) href = '#';
    const isExternal = /^https?:\/\//i.test(href);
    const titleAttr = title ? ` title="${title}"` : '';
    const targetAttr = isExternal ? ' target="_blank" rel="noopener noreferrer"' : '';
    return `<a href="${href}"${titleAttr}${targetAttr}>${text || href}</a>`;
  };
  const rawHtml = marked.parse(text, { renderer }) as string;
  const purifier = getPurifier();
  // If DOMPurify is unavailable (e.g. SSR), strip all HTML tags as a safe fallback
  const result = purifier
    ? purifier.sanitize(rawHtml, {
        // DOMPurify strips `target` by default; we intentionally allow it for safe external links.
        ADD_ATTR: ['target', 'rel'],
      })
    : rawHtml.replace(/<\/?[^>]+(>|$)/g, '');
  // Only cache when highlighter is loaded to allow re-renders to pick up highlighting
  if (hljsModule) {
    if (renderCache.size >= CACHE_MAX) {
      // Evict oldest entry
      const firstKey = renderCache.keys().next().value;
      if (firstKey !== undefined) renderCache.delete(firstKey);
    }
    renderCache.set(text, result);
  }
  return result;
}

export interface CodeBlock {
  language: string;
  code: string;
}

export function extractCodeBlocks(text: string): CodeBlock[] {
  if (!text) return [];
  const blocks: CodeBlock[] = [];
  const regex = /```(\w+)?\n([\s\S]*?)```/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    blocks.push({
      language: match[1] || 'text',
      code: match[2].trim(),
    });
  }

  return blocks;
}

export function hasMarkdown(text: string): boolean {
  if (!text) return false;
  return /(\*\*|__|`|```|\[.*?\]\(.*?\)|^>|\n#{1,6}\s|^\s*[-*+]\s|^\s*\d+\.\s)/m.test(text);
}
